import { useMemo } from 'react'
import { useIsContractAddress } from '~/hooks'
import {
  useSpotSwapPair,
  useAllSpotSwapTokens,
  useSpotSwapUserConfig,
} from '~/state/dex/hooks'
import { constructTokenId, getIsNativeToken, getIsWeth } from '~/utils/token'
import { formatETHCrossChain } from '~/components/Exchange/helpers'

export const useCrossTokens = (selectedChainId: number | null) => {
  const [inputToken, outputToken] = useSpotSwapPair()
  let inputCrossChain = inputToken?.crossChain
  const isNativeToken = getIsNativeToken(inputToken?.address)
  const isWeth = getIsWeth(inputToken?.address, inputToken?.chainId)
  const allTokens = useAllSpotSwapTokens()

  const { recipient } = useSpotSwapUserConfig()
  const isContractAddress = useIsContractAddress(
    recipient,
    outputToken?.chainId,
  )

  // crossChain 是新增字段，inputToken 从 spotSwapPair 缓存中读取，可能缺少该字段。如果没有就从 tokens 中查找
  if (!inputCrossChain) {
    const inputTokenId = constructTokenId(
      inputToken?.chainId,
      inputToken?.address,
    )
    inputCrossChain = allTokens.find((item) => {
      const itemTokenId = constructTokenId(item.chainId, item.address)
      return itemTokenId === inputTokenId
    })?.crossChain
  }

  const formattedCrossChain = useMemo(() => {
    if (isNativeToken || isWeth) {
      return formatETHCrossChain(inputCrossChain, isContractAddress)
    }
    return inputCrossChain
  }, [inputCrossChain, isNativeToken, isWeth, isContractAddress])

  const tokens = useMemo(() => {
    const isSelectAll = selectedChainId === null
    const isSelectInputNetwork = selectedChainId === inputToken?.chainId
    if (isSelectAll || isSelectInputNetwork) return allTokens
    return allTokens?.filter((token) => token.chainId === selectedChainId)
  }, [allTokens, inputToken?.chainId, selectedChainId])

  const crossTokens = useMemo(() => {
    const isNoTokens = !tokens || tokens.length === 0
    const isNoInputCrossChain =
      !formattedCrossChain || formattedCrossChain.length === 0
    if (isNoTokens || isNoInputCrossChain) return []

    const tokenMap = new Map(
      tokens.map((token) => [
        constructTokenId(token.chainId, token.address),
        token,
      ]),
    )

    const result = formattedCrossChain.reduce((acc, item) => {
      const { destinationChainId, destinationToken } = item

      const itemId = constructTokenId(destinationChainId, destinationToken)
      const token = tokenMap.get(itemId)
      if (token) {
        acc.push(token)
      }
      return acc
    }, [])
    return result
  }, [tokens, formattedCrossChain])

  return crossTokens
}
